import React, { useEffect, useState, useMemo } from "react";
import PopupModules from "src/features/auth/components/PopupModules";
import InputGroup from "../customer/InputGroup";
import useEmployee from "src/business-types/grocery/hooks/useEmployee";
import { notifyError } from "src/assets/toastService";
import PermissionTable from "./PermissionTable";
import CommonButtons from "src/shared/components/CommonButtons";
import { User, Address } from "src/shared/types";

type RolePermissions = Record<string, string[]>;

type Props = {
  open: boolean;
  onClose: () => void;
  editData?: User | null;
  onSuccess?: () => void;
  fullUserData?: User | null;
};

type EmployeeEditPayload = {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  userName?: string;
  phones?: { phone: string; isPrimary: boolean }[];
  emails?: { email: string; isPrimary: boolean }[];
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  dateOfJoining?: string;
  roles?: string[];
  additionalPermissions?: RolePermissions;
  removedPermissions?: RolePermissions;
};

function EmployeeModal({ open, onClose, editData, onSuccess }: Props) {
  const { saveEmployee, changeEmployee } = useEmployee();

  const [form, setForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    username: "",
    phone: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    state: "",
    postalCode: "",
    doj: "",
    roles: "",
    role_permissions: {} as RolePermissions,
    additionalPermissions: {} as RolePermissions,
    removedPermissions: {} as RolePermissions,
    permissions: {} as RolePermissions,
  });

  const [disableBtn, setDisableBtn] = useState(false);
  const roleList = ["Admin", "User"];
  const [errors, setErrors] = useState({ phone: "", email: "" });
  const [activeTab, setActiveTab] = useState<"user" | "address" | "permission">("user");

  /* ---------------- HELPERS ---------------- */

  const mergePermissions = (
    role: RolePermissions = {},
    additional: RolePermissions = {},
    removed: RolePermissions = {}
  ): RolePermissions => {
    const result: RolePermissions = {};
    const modules = new Set([...Object.keys(role), ...Object.keys(additional), ...Object.keys(removed)]);

    modules.forEach((module) => {
      const roleActions = role[module] || [];
      const additionalActions = additional[module] || [];
      const removedActions = removed[module] || [];
      const combined = new Set([...roleActions, ...additionalActions]);
      removedActions.forEach((a) => combined.delete(a));
      result[module] = Array.from(combined);
    });

    return result;
  };

  const splitPermissions = (final: RolePermissions, role: RolePermissions) => {
    const additional: RolePermissions = {};
    const removed: RolePermissions = {};
    const modules = new Set([...Object.keys(final), ...Object.keys(role)]);

    modules.forEach((module) => {
      const finalActions = (final[module] || []).sort();
      const roleActions = (role[module] || []).sort();

      const add = finalActions.filter((a) => !roleActions.includes(a));
      const rem = roleActions.filter((a) => !finalActions.includes(a));

      if (add.length > 0) additional[module] = add;
      if (rem.length > 0) removed[module] = rem;
    });

    return { additionalPermissions: additional, removedPermissions: removed };
  };

  /* ---------------- EXTRACT DATA HELPERS ---------------- */

  const extractPhone = (data: User): string => {
    if (data.phones && Array.isArray(data.phones) && data.phones.length > 0) {
      const phone = data.phones[0]?.phone || "";
      if (phone && !["N/A", "null", "undefined"].includes(phone)) return phone;
    }
    if ((data as any).phoneNumber && !["N/A", "null", "undefined"].includes((data as any).phoneNumber)) {
      return (data as any).phoneNumber;
    }
    if ((data as any).phone && (data as any).phone !== "N/A") return (data as any).phone;
    return "";
  };

  const extractEmail = (data: User): string => {
    if (data.emails && Array.isArray(data.emails) && data.emails.length > 0) {
      const firstEmail = data.emails[0];
      if (typeof firstEmail === "object" && firstEmail !== null && "email" in firstEmail) {
        return firstEmail.email || "";
      }
      return (firstEmail as any) || "";
    }
    return "";
  };

  const extractRole = (data: User): string => {
    if (data.roles && Array.isArray(data.roles) && data.roles.length > 0) return data.roles[0] || "";
    if (typeof data.roles === "string") return data.roles;
    return "";
  };

  const extractAddressField = (data: User, field: keyof Address): string => {
    if ((data as any)[field]) return (data as any)[field] || "";
    if (data.addresses && (data.addresses as any)[field]) return (data.addresses as any)[field] || "";
    return "";
  };

  /* ---------------- LOAD EDIT DATA ---------------- */

  useEffect(() => {
    setActiveTab("user");

    if (editData) {
      const rolePerm: RolePermissions = editData.permissions?.[0] || {};
      const additional: RolePermissions = editData.additionalPermissions || {};
      const removed: RolePermissions = editData.removedPermissions || {};

      setForm({
        firstName: editData.firstName || "",
        middleName: editData.middleName || "",
        lastName: editData.lastName || "",
        username: editData.userName || "",
        phone: extractPhone(editData),
        email: extractEmail(editData),
        addressLine1: extractAddressField(editData, "addressLine1"),
        addressLine2: extractAddressField(editData, "addressLine2"),
        addressLine3: extractAddressField(editData, "addressLine3"),
        city: extractAddressField(editData, "city"),
        state: extractAddressField(editData, "state"),
        postalCode: extractAddressField(editData, "postalCode"),
        doj: editData.dateOfJoining || "",
        roles: extractRole(editData),
        role_permissions: rolePerm,
        additionalPermissions: additional,
        removedPermissions: removed,
        permissions: mergePermissions(rolePerm, additional, removed),
      });
    } else {
      reset();
    }
  }, [editData, open]);

  /* ---------------- CHANGE DETECTION ---------------- */

  const hasChanges = useMemo(() => {
    if (!editData) return true; // Always allow submission when creating a new record

    const originalFields = {
      firstName: editData.firstName || "",
      middleName: editData.middleName || "",
      lastName: editData.lastName || "",
      username: editData.userName || "",
      phone: extractPhone(editData),
      email: extractEmail(editData),
      addressLine1: extractAddressField(editData, "addressLine1"),
      addressLine2: extractAddressField(editData, "addressLine2"),
      addressLine3: extractAddressField(editData, "addressLine3"),
      city: extractAddressField(editData, "city"),
      state: extractAddressField(editData, "state"),
      postalCode: extractAddressField(editData, "postalCode"),
      doj: editData.dateOfJoining || "",
      roles: extractRole(editData),
    };

    const isFormChanged = Object.entries(originalFields).some(
      ([key, value]) => (form[key as keyof typeof originalFields] || "") !== value
    );

    const { additionalPermissions, removedPermissions } = splitPermissions(form.permissions, form.role_permissions);

    const origAdd = JSON.stringify(editData.additionalPermissions || {});
    const origRem = JSON.stringify(editData.removedPermissions || {});
    const newAdd = JSON.stringify(additionalPermissions);
    const newRem = JSON.stringify(removedPermissions);

    const arePermissionsChanged = origAdd !== newAdd || origRem !== newRem;

    return isFormChanged || arePermissionsChanged;
  }, [form, editData]);

  /* ---------------- INPUT CHANGE ---------------- */

  const handleChange = (name: string, value: string) => {
    let v = value;

    if (name === "phone") {
      v = v.replace(/\D/g, "");
      if (v.length < 8 || v.length > 12) {
        setErrors((p) => ({ ...p, phone: "Phone must be 8-12 digits" }));
      } else setErrors((p) => ({ ...p, phone: "" }));
    }

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.(com|in|co)$/i;
      if (!emailRegex.test(v)) {
        setErrors((p) => ({ ...p, email: "Invalid email format" }));
      } else setErrors((p) => ({ ...p, email: "" }));
    }

    setForm((prev) => ({ ...prev, [name]: v }));
  };

  /* ---------------- VALIDATION ---------------- */

  const isFormValid = () =>
    !!(form.firstName && form.username && form.phone && form.email && form.doj && form.roles && !errors.phone && !errors.email);

  /* ---------------- SUBMIT ---------------- */

  const submit = () => {
    if (!isFormValid()) {
      notifyError("Please fix validation errors");
      return;
    }

    const { additionalPermissions, removedPermissions } = splitPermissions(form.permissions, form.role_permissions);

    if (editData) {
      const basePayload = {
        firstName: form.firstName,
        middleName: form.middleName,
        lastName: form.lastName,
        userName: form.username,
        phones: form.phone ? [{ phone: form.phone, isPrimary: true }] : [],
        emails: form.email ? [{ email: form.email, isPrimary: true }] : [],
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        addressLine3: form.addressLine3,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        dateOfJoining: form.doj,
        roles: form.roles ? [form.roles] : [],
      };

      const original = {
        firstName: editData.firstName || "",
        middleName: editData.middleName || "",
        lastName: editData.lastName || "",
        userName: editData.userName || "",
        phones: editData.phones || [],
        emails: editData.emails || [],
        addressLine1: extractAddressField(editData, "addressLine1"),
        addressLine2: extractAddressField(editData, "addressLine2"),
        addressLine3: extractAddressField(editData, "addressLine3"),
        city: extractAddressField(editData, "city"),
        state: extractAddressField(editData, "state"),
        postalCode: extractAddressField(editData, "postalCode"),
        dateOfJoining: editData.dateOfJoining || "",
        roles: editData.roles || [],
      };

      const editPayload: Partial<typeof basePayload> & {
        additionalPermissions?: RolePermissions;
        removedPermissions?: RolePermissions;
      } = {};

      (Object.keys(basePayload) as (keyof typeof basePayload)[]).forEach((key) => {
        if (JSON.stringify(basePayload[key]) !== JSON.stringify(original[key])) {
          editPayload[key as keyof EmployeeEditPayload] = basePayload[key] as any;
        }
      });

      const origAdd = JSON.stringify(editData.additionalPermissions || {});
      const origRem = JSON.stringify(editData.removedPermissions || {});
      if (JSON.stringify(additionalPermissions) !== origAdd || JSON.stringify(removedPermissions) !== origRem) {
        editPayload.additionalPermissions = additionalPermissions;
        editPayload.removedPermissions = removedPermissions;
      }

      if (Object.keys(editPayload).length === 0) {
        notifyError("No changes detected");
        return;
      }

      setDisableBtn(true);

      changeEmployee(
        editData._id.$oid,
        editPayload,
        () => {
          setDisableBtn(false);
          onClose();
          reset();
          onSuccess?.();
        },
        () => setDisableBtn(false)
      );

      return;
    }

    setDisableBtn(true);

    const payload = {
      userName: form.username,
      firstName: form.firstName,
      middleName: form.middleName,
      lastName: form.lastName,
      phones: form.phone ? [{ phone: form.phone }] : [],
      emails: form.email ? [{ email: form.email }] : [],
      dob: form.doj,
      dateOfJoining: form.doj,
      roles: form.roles ? [form.roles] : [],
      address: {
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        addressLine3: form.addressLine3,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
      },
      additionalPermissions,
      removedPermissions,
    };

    saveEmployee(
      payload,
      () => {
        setDisableBtn(false);
        onClose();
        reset();
        onSuccess?.();
      },
      () => setDisableBtn(false)
    );
  };

  /* ---------------- RESET ---------------- */

  const reset = () => {
    setForm({
      firstName: "",
      middleName: "",
      lastName: "",
      username: "",
      phone: "",
      email: "",
      addressLine1: "",
      addressLine2: "",
      addressLine3: "",
      city: "",
      state: "",
      postalCode: "",
      doj: "",
      roles: "",
      role_permissions: {},
      additionalPermissions: {},
      removedPermissions: {},
      permissions: {},
    });
    setErrors({ phone: "", email: "" });
  };

  /* ---------------- UI ---------------- */

  return (
    <PopupModules
      isOpen={open}
      onClose={() => {
        onClose();
        reset();
      }}
      title={editData ? "Edit Employee" : "Add Employee"}
      className="w-[60%] h-fit"
    >
      <div className="py-2 w-full h-[85%]">
        {/* Tab Bar */}
        <div className="flex border-b mb-4">
          <button
            className={`px-4 py-2 text-sm ${activeTab === "user" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
            onClick={() => setActiveTab("user")}
          >
            User Details
          </button>

          <button
            className={`px-4 py-2 text-sm ${activeTab === "address" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
            onClick={() => setActiveTab("address")}
          >
            Address
          </button>

          {editData && (
            <button
              className={`px-4 py-2 text-sm ${activeTab === "permission" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
              onClick={() => setActiveTab("permission")}
            >
              Permissions
            </button>
          )}
        </div>

        {/* User Details Tab */}
        {activeTab === "user" && (
          <div className="grid grid-cols-2 gap-4">
            <InputGroup label="First Name" type="text" value={form.firstName} name="firstName" onChange={handleChange} mandatory />
            <InputGroup label="Middle Name" type="text" value={form.middleName} name="middleName" onChange={handleChange} />
            <InputGroup label="Last Name" type="text" value={form.lastName} name="lastName" onChange={handleChange} />
            <InputGroup label="Username" type="text" value={form.username} name="username" onChange={handleChange} mandatory disabled={!!editData} />

            <div>
              <InputGroup label="Phone" type="number" value={form.phone} name="phone" onChange={handleChange} mandatory />
              {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
            </div>

            <div>
              <InputGroup label="Email" type="email" value={form.email} name="email" onChange={handleChange} mandatory />
              {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
            </div>

            <InputGroup label="Date Of Joining" type="date" value={form.doj} name="doj" onChange={handleChange} mandatory />

            <select value={form.roles} onChange={(e) => handleChange("roles", e.target.value)} className="border rounded-md px-3 py-2 text-sm">
              <option value="">Select Role</option>
              {roleList.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Address Tab */}
        {activeTab === "address" && (
          <div className="grid grid-cols-2 gap-4">
            <InputGroup label="Address Line 1" type="text" value={form.addressLine1} name="addressLine1" onChange={handleChange} />
            <InputGroup label="Address Line 2" type="text" value={form.addressLine2} name="addressLine2" onChange={handleChange} />
            <InputGroup label="Address Line 3" type="text" value={form.addressLine3} name="addressLine3" onChange={handleChange} />
            <InputGroup label="City" type="text" value={form.city} name="city" onChange={handleChange} />
            <InputGroup label="State" type="text" value={form.state} name="state" onChange={handleChange} />
            <InputGroup label="Postal Code" type="text" value={form.postalCode} name="postalCode" onChange={handleChange} />
          </div>
        )}

        {/* Permissions Tab (edit only) */}
        {activeTab === "permission" && editData && (
          <PermissionTable
            permissions={form.permissions}
            setPermissions={(data) =>
              setForm((prev) => ({
                ...prev,
                permissions: typeof data === "function" ? data(prev.permissions) : data,
              }))
            }
          />
        )}
      </div>

      <div className="flex justify-end gap-4 pb-4">
        <CommonButtons
          label="Cancel"
          variant="outlineDanger"
          onClick={() => {
            onClose();
            reset();
          }}
        />
        <CommonButtons
          label={editData ? "Update" : "Confirm"}
          onClick={submit}
          disabled={!isFormValid() || disableBtn || (!!editData && !hasChanges)}
        />
      </div>
    </PopupModules>
  );
}

export default EmployeeModal;